import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { UserEquipment, EquipmentType, EQUIPMENT_TYPES, EQUIPMENT_TYPE_LABELS, EQUIPMENT_SERVICE_INTERVALS, EQUIPMENT_REQUIRES_SERVICE } from '../types/Equipment';
import { equipmentService } from '../services/equipmentService';
import { postService, PostVisibility } from '../services/postService';
import { preferencesManager } from '../utils/preferencesManager';
import { authService } from '../services/authService';
import EquipmentTypeIcon from './EquipmentTypeIcon';

interface EquipmentDialogProps {
  open: boolean;
  equipment: UserEquipment | null;
  onClose: () => void;
  onSaved: (equipment: UserEquipment) => void;
}

const EquipmentDialog: React.FC<EquipmentDialogProps> = ({
  open,
  equipment,
  onClose,
  onSaved,
}) => {
  const [equipmentType, setEquipmentType] = useState<EquipmentType>('BCD');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [isServiceable, setIsServiceable] = useState(false);
  const [serviceIntervalMonths, setServiceIntervalMonths] = useState('');
  const [notes, setNotes] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [postToFeed, setPostToFeed] = useState(false);
  const [postVisibility, setPostVisibility] = useState<string>(PostVisibility.Buddies);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (equipment) {
      // Editing existing equipment
      setEquipmentType(equipment.equipmentType);
      setBrand(equipment.brand || '');
      setModel(equipment.model || '');
      setSerialNumber(equipment.serialNumber || '');
      setPurchaseDate(equipment.purchaseDate ? new Date(equipment.purchaseDate).toISOString().split('T')[0] : '');
      setIsServiceable(equipment.isServiceable || false);
      setServiceIntervalMonths(equipment.serviceIntervalMonths?.toString() || '');
      setNotes(equipment.notes || '');
      setIsDefault(equipment.isDefault || false);
    } else {
      // Adding new equipment
      resetForm();
    }
  }, [equipment, open]);

  // Auto-set defaults when equipment type changes (only for new equipment)
  useEffect(() => {
    if (!equipment && open) {
      // Set isServiceable based on equipment type
      const requiresService = EQUIPMENT_REQUIRES_SERVICE[equipmentType];
      setIsServiceable(requiresService);

      // Set default service interval if applicable
      const defaultInterval = EQUIPMENT_SERVICE_INTERVALS[equipmentType];
      if (defaultInterval !== null && requiresService) {
        setServiceIntervalMonths(defaultInterval.toString());
      } else {
        setServiceIntervalMonths('');
      }
    }
  }, [equipmentType, equipment, open]);

  const resetForm = () => {
    setEquipmentType('BCD');
    setBrand('');
    setModel('');
    setSerialNumber('');
    setPurchaseDate('');
    setIsServiceable(false);
    setServiceIntervalMonths('');
    setNotes('');
    setIsDefault(false);
    setPostToFeed(false);
    setPostVisibility(PostVisibility.Buddies);
  };

  const handleSubmit = async () => {
    // preferencesManager's userId is only set as a side effect of certain pages having
    // already mounted this session — fall back to authService (the actual source of truth
    // for "who's signed in"), same fallback EquipmentManagement.tsx already uses to load
    // this list in the first place.
    const userId = preferencesManager.getUserId() ?? authService.getUser()?.userId ?? null;
    if (!userId) {
      alert('Please set up your user account first');
      return;
    }

    try {
      setLoading(true);

      const equipmentData: Partial<UserEquipment> = {
        userId,
        equipmentType,
        brand: brand.trim() || undefined,
        model: model.trim() || undefined,
        serialNumber: serialNumber.trim() || undefined,
        purchaseDate: purchaseDate ? new Date(purchaseDate).toISOString() : undefined,
        isServiceable,
        serviceIntervalMonths: isServiceable && serviceIntervalMonths ? parseInt(serviceIntervalMonths, 10) : undefined,
        notes: notes.trim() || undefined,
        isDefault,
      };

      let savedEquipment: UserEquipment;

      if (equipment) {
        // Update existing equipment
        const updatedEquipment: UserEquipment = {
          ...equipment,
          ...equipmentData,
        };
        savedEquipment = await equipmentService.updateEquipment(userId, equipment.equipmentId, updatedEquipment);
      } else {
        // Create new equipment
        savedEquipment = await equipmentService.createEquipment(equipmentData);

        if (postToFeed) {
          try {
            await postService.createEquipmentPost({
              equipmentId: savedEquipment.equipmentId,
              visibility: postVisibility,
            });
          } catch (postError) {
            // The equipment itself saved fine; a failed announcement shouldn't block that.
            console.error('Failed to post equipment to feed:', postError);
          }
        }
      }

      onSaved(savedEquipment);

      handleClose();
    } catch (error) {
      console.error('Failed to save equipment:', error);
      alert('Failed to save equipment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isFormValid = () => {
    return equipmentType.trim() !== '';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{equipment ? 'Edit Equipment' : 'Add Equipment'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Equipment Type */}
          <FormControl fullWidth required>
            <InputLabel>Equipment Type</InputLabel>
            <Select
              value={equipmentType}
              label="Equipment Type"
              onChange={(e) => setEquipmentType(e.target.value as EquipmentType)}
            >
              {EQUIPMENT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <EquipmentTypeIcon type={type} size={20} />
                    {EQUIPMENT_TYPE_LABELS[type]}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Brand */}
          <TextField
            label="Brand"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="e.g., Scubapro, Aqua Lung"
            fullWidth
          />

          {/* Model */}
          <TextField
            label="Model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="e.g., MK25 EVO"
            fullWidth
          />

          {/* Serial Number */}
          <TextField
            label="Serial Number"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            placeholder="Equipment serial number"
            fullWidth
          />

          {/* Purchase Date */}
          <TextField
            label="Purchase Date"
            type="date"
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
          />

          {/* Serviceable Checkbox */}
          <Box sx={{ backgroundColor: 'action.hover', p: 2, borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isServiceable}
                  onChange={(e) => setIsServiceable(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Requires Service/Maintenance
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Enable if this equipment needs regular servicing (e.g., regulators, BCDs)
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Service Interval - Only show if serviceable */}
          {isServiceable && (
            <TextField
              label="Service Interval (months)"
              type="number"
              value={serviceIntervalMonths}
              onChange={(e) => setServiceIntervalMonths(e.target.value)}
              placeholder={EQUIPMENT_SERVICE_INTERVALS[equipmentType] ? `Recommended: ${EQUIPMENT_SERVICE_INTERVALS[equipmentType]}` : "e.g., 12"}
              helperText={
                EQUIPMENT_SERVICE_INTERVALS[equipmentType]
                  ? `Industry standard: ${EQUIPMENT_SERVICE_INTERVALS[equipmentType]} months (${EQUIPMENT_SERVICE_INTERVALS[equipmentType] === 12 ? 'annually' : EQUIPMENT_SERVICE_INTERVALS[equipmentType] === 24 ? 'every 2 years' : EQUIPMENT_SERVICE_INTERVALS[equipmentType] + ' months'})`
                  : "Number of months between services"
              }
              fullWidth
              inputProps={{ min: 0 }}
            />
          )}

          {/* Notes */}
          <TextField
            label="Notes"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes about this equipment..."
            fullWidth
          />

          {/* Default Equipment Checkbox */}
          <Box sx={{ backgroundColor: 'action.hover', p: 2, borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Mark as Default Equipment
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    When enabled, this equipment will automatically be attached to new dives
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Post to Feed - only offered when adding new equipment, not when editing */}
          {!equipment && (
            <Box sx={{ backgroundColor: 'action.hover', p: 2, borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={postToFeed}
                    onChange={(e) => setPostToFeed(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Share to Feed
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Let your buddies know about your new gear
                    </Typography>
                  </Box>
                }
              />
              {postToFeed && (
                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                  <InputLabel>Who can see this</InputLabel>
                  <Select
                    value={postVisibility}
                    label="Who can see this"
                    onChange={(e) => setPostVisibility(e.target.value)}
                  >
                    <MenuItem value={PostVisibility.Public}>Public</MenuItem>
                    <MenuItem value={PostVisibility.Buddies}>Dive Buddies only</MenuItem>
                    <MenuItem value={PostVisibility.Followers}>Followers only</MenuItem>
                    <MenuItem value={PostVisibility.Private}>Only me</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid()}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : equipment ? 'Update' : 'Add Equipment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipmentDialog;
